package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import dev.lingvo.shared.CanvasSlotMarkDto
import dev.lingvo.shared.CanvasSlotMarkState
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.screenshotRenderImage
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.url.URL
import org.w3c.dom.events.EventListener

/** Consent controls wait for the exact persisted render to load, rather than relying on an editable canvas preview. */
@Composable
internal fun CanvasMarkPreview(
    slug: String, platform: StorePlatform, marks: List<CanvasSlotMarkDto>, render: RenderDto?,
    writable: Boolean, busy: Boolean, refreshEpoch: Int, onMark: (CanvasSlotMarkState) -> Unit,
) {
    val subject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    var url by remember(subject, platform, render?.id, render?.sha256, refreshEpoch) { mutableStateOf<String?>(null) }
    var ready by remember(subject, platform, render?.id, render?.sha256, refreshEpoch) { mutableStateOf(false) }
    var failed by remember(subject, platform, render?.id, render?.sha256, refreshEpoch) { mutableStateOf(false) }
    LaunchedEffect(subject, platform, render?.id, render?.sha256, refreshEpoch) {
        val current = render ?: return@LaunchedEffect
        val action = subject?.action ?: return@LaunchedEffect
        var owned: String? = null
        try {
            val blob = requestProjectResource(action, slug) { ApiClient.screenshotRenderImage(slug, platform, current.id) }
            owned = URL.createObjectURL(blob)
            url = owned
            kotlinx.coroutines.awaitCancellation()
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { failed = true }
        finally { owned?.let(URL::revokeObjectURL) }
    }
    url?.let { source -> key(source) { Img(source, alt = t("app.projects.slug.screenshots.canvas_mark_preview.saved_screenshot_being_reviewed", "Saved screenshot being reviewed"), attrs = {
        attr("referrerpolicy", "no-referrer")
        ref { image ->
            val loaded = EventListener { ready = image.naturalWidth > 0; failed = !ready }
            val failure = EventListener { failed = true; ready = false }
            image.addEventListener("load", loaded)
            image.addEventListener("error", failure)
            if (image.complete) { ready = image.naturalWidth > 0; failed = !ready }
            onDispose { image.removeEventListener("load", loaded); image.removeEventListener("error", failure) }
        }
        style { property("max-width", "100%"); property("max-height", "400px"); property("height", "auto"); property("object-fit", "contain") }
    }) } }
    if (failed) LMuted(t("app.projects.slug.screenshots.canvas_mark_preview.the_saved_screenshot_could_not_load", "The saved screenshot could not load. Refresh before marking it."))
    CanvasReviewMarks(marks, render, writable && ready && !failed, busy, onMark)
}
